import { PlayerIdentity as RatedPlayerIdentity } from 'components/ReviewAudit/PublicRatings'
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap"
import { Link } from "react-router-dom"
import moment from "moment"
import { capitalize } from "lodash"

import defaultProfilePicture from "../../../assets/images/users/default-profile-picture.png"
import PlayerImpersonateAction from "./PlayerImpersonateAction"
import CopyContact from "components/Common/CopyContact"
import { accountPersonas, childrenText, holdsPersona, personaLabel, personaTone } from "./personas"
import { LEVELS, childNumber } from "pages/Tenis10Claims/claimsLabels"
import "assets/scss/custom/components/_directoryActions.scss"

const EMPTY_VALUE = "Not set"

export const calculateAge = (birthDate, referenceDate = moment()) => {
  // Sportya age uses calendar years; preserve the recorded birth year across time zones.
  const date = moment.parseZone(birthDate)

  return birthDate && date.isValid() ? referenceDate.year() - date.year() : null
}

export const getSportLevels = (user, sportType) => ({
  singles: user?.sports?.[sportType]?.singles?.gameLevel ?? null,
  doubles: user?.sports?.[sportType]?.doubles?.gameLevel ?? null,
})

const formatLocation = user => user?.location?.name || EMPTY_VALUE

const PlayerIdentity = ({ user, listState }) => {
  const age = calculateAge(user?.birthDate)
  const gender = user?.gender ? capitalize(user.gender) : EMPTY_VALUE
  const profilePicture = user?.profilePicture
    ? `${process.env.REACT_APP_API_URL}/assets/accounts/${user.userId}/${user.profilePicture}`
    : defaultProfilePicture

  return (
    <div className="player-identity-cell">
      <img
        src={profilePicture}
        alt=""
        className="player-list-avatar"
        loading="lazy"
      />
      <div className="min-w-0">
        <RatedPlayerIdentity player={user} to={{ pathname: `/accounts/players/${user.id}`, state: listState }} className="player-list-name text-dark" />
        <div className="player-list-meta">
          <span>#{user.userId}</span>
          <span className="player-list-location" title={formatLocation(user)}>{formatLocation(user)}</span>
        </div>
        <div className="player-list-meta">
          <span>{age === null ? "Age not set" : `${age} years`}</span>
          <span>{gender}</span>
        </div>
        <AccountPersonas user={user} />
      </div>
    </div>
  )
}

// P28 (A2) + P50 (Tudor, 2026-10-24): the children an administrator's search found, as cards in a full-width row under their
// parent's row — the way the events list shows an event's competitions; each card the same: the name, then the age · category
// · level and the passport badge, short, on the right. RELEASE-TENIS10 § 11 P78: the child's own number after its name, as its
// parent's „#990044” reads above
const PASSPORT_TONE = { active: "success", expiring: "warning", inactive: "danger" }
const childFacts = child => [child.age != null && `${child.age} yrs`, child.ageCategory, LEVELS[child.tenis10Level] || child.tenis10Level].filter(Boolean)
const passportText = child => (child.passportStatus === "inactive" ? "Inactive"
  : `${child.passportEndDate ? new Date(child.passportEndDate).getUTCFullYear() : ""} ${child.passportStatus}`.trim())
const PassportBadge = ({ child }) => <span className={`badge badge-soft-${PASSPORT_TONE[child.passportStatus] || "secondary"} player-child-passport`}>
  <i className="bx bx-id-card" aria-hidden="true" /> {passportText(child)}
</span>
export const hasChildrenFound = user => Array.isArray(user?.matchedChildren) && user.matchedChildren.length > 0
export const MatchedChildrenStrip = ({ user }) => {
  const children = user?.matchedChildren || []
  return <section className="player-children-strip" aria-label={`Children found for ${user.firstName} ${user.lastName}`}>
    <div className="player-children-heading"><strong>{children.length} {children.length === 1 ? "child" : "children"} found</strong>
      {user.childrenCount > children.length && <small className="text-muted">of {user.childrenCount}</small>}</div>
    <div className="player-children-grid">
      {children.map(child => <div key={child.profileId} className="player-child-card">
        <strong className="player-child-name">{child.firstName} {child.lastName}
          {childNumber(child) != null && <> <small className="text-muted font-weight-normal">#{childNumber(child)}</small></>}</strong>
        <span className="player-child-secondary">
          <small className="text-muted">{childFacts(child).join(" · ")}</small>
          <PassportBadge child={child} />
        </span>
      </div>)}
    </div>
  </section>
}

// PRD 16 LR16 (LR10 verdict L-A) — what the account is: its personas and, for a parent, how many children it manages
const AccountPersonas = ({ user }) => (
  <div className="player-list-personas mt-1">
    {accountPersonas(user).map(persona => (
      <span key={persona} className={`badge badge-soft-${personaTone(persona)} mr-1`}>{personaLabel(persona)}</span>
    ))}
    {user?.childrenCount > 0 && <span className="text-muted font-size-12">{childrenText(user.childrenCount)}</span>}
  </div>
)

const PlayerContact = ({ user }) => (
  <div className="player-contact-cell">
    {user?.email ? (
      <CopyContact key={user.email} value={user.email} label="email" icon="bx bx-envelope" />
    ) : (
      <span className="text-muted">Email not set</span>
    )}
    {user?.phone ? (
      <CopyContact key={user.phone} value={user.phone} label="phone" icon="bx bx-phone" />
    ) : (
      <span className="text-muted">Phone not set</span>
    )}
  </div>
)

const SportLevels = ({ label, levels }) => {
  const hasLevel = levels.singles !== null || levels.doubles !== null

  return (
    <div className="player-sport-levels">
      <span className="player-sport-name">{label}</span>
      <span className={hasLevel ? "text-dark" : "text-muted"}>
        {hasLevel
          ? `S${levels.singles ?? "–"} · D${levels.doubles ?? "–"}`
          : EMPTY_VALUE}
      </span>
    </div>
  )
}

// an account without the Player persona has no competitive profile (LR10 verdict L-A)
const CompetitiveProfile = ({ user }) => (holdsPersona(user, "player")
  ? <div className="player-competitive-cell">
    <SportLevels label="Tennis" levels={getSportLevels(user, "tennis")} />
    <SportLevels label="Padel" levels={getSportLevels(user, "padel")} />
  </div>
  : <span className="text-muted">Not a player</span>)

const statusClass = status => {
  if (status === "activated") return "success"
  if (status === "pending") return "info"
  if (status === "blocked") return "warning"
  return "danger"
}

const AccountSummary = ({ user }) => {
  const membership = capitalize(user?.membership?.plan || "free")

  return (
    <div className="player-account-cell">
      <div>
        <span className={`badge badge-${statusClass(user?.status)}`}>
          {capitalize(user?.status || "unknown")}
        </span>
        <span
          className={`badge ml-1 ${
            membership.toLowerCase() === "premium"
              ? "badge-soft-primary"
              : "badge-soft-secondary"
          }`}
        >
          {membership}
        </span>
      </div>
      <span className="text-muted font-size-12">
        Joined{" "}
        {user?.createdAt ? moment(user.createdAt).format("D MMM YYYY") : "—"}
      </span>
    </div>
  )
}

const PlayerActions = ({ account, listState }) => (
  <div className="player-actions-cell directory-row-actions">
    <Link
      to={{ pathname: `/accounts/players/${account.id}`, state: listState }}
      className="btn btn-outline-primary btn-sm directory-action-button"
      aria-label={`Open user: ${account.firstName || ""} ${account.lastName || ""}`.trim()}
    >
      Open
    </Link>
    <UncontrolledDropdown>
      <DropdownToggle
        tag="button"
        type="button"
        className="btn btn-light btn-sm directory-action-button"
        aria-label={`More actions for ${account.firstName} ${account.lastName}`}
      >
        <i className="mdi mdi-dots-horizontal" aria-hidden="true" />
      </DropdownToggle>
      <DropdownMenu right>
        <PlayerImpersonateAction account={account} dropdown />
        <DropdownItem tag={Link} to={{ pathname: `/accounts/players/${account.id}/edit`, state: listState }}>
          Edit user
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  </div>
)

const listColumns = (adminUserId, listState) => [
  {
    dataField: "userId",
    text: "User",
    sort: true,
    headerStyle: { width: "27%" },
    formatter: (cellContent, user) => <PlayerIdentity user={user} listState={listState} />,
  },
  {
    dataField: "email",
    text: "Contact",
    headerStyle: { width: "23%" },
    formatter: (cellContent, user) => <PlayerContact user={user} />,
  },
  {
    dataField: "levels",
    text: "Competitive profile",
    headerStyle: { width: "20%" },
    formatter: (cellContent, user) => <CompetitiveProfile user={user} />,
  },
  {
    dataField: "createdAt",
    text: "Account",
    sort: true,
    headerStyle: { width: "18%" },
    formatter: (cellContent, user) => <AccountSummary user={user} />,
  },
  {
    dataField: "menu",
    isDummyField: true,
    text: "Actions",
    headerAlign: "right",
    align: "right",
    headerStyle: { width: "12%" },
    formatter: (cellContent, account) => (
      <PlayerActions account={account} adminUserId={adminUserId} listState={listState} />
    ),
  },
]

export default listColumns
